import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReassuranceRow } from '@/components/patterns/ReassuranceRow';

export interface SummaryLine {
  label: string;
  value: string | null | undefined;
}

// Desktop sidebar on steps 1 and 2: what's chosen so far, the total and the
// step's main button.
export function SummaryRail({ rows, total, cta, cancellationHours }: {
  rows: SummaryLine[];
  total?: string;
  cta: ReactNode;
  cancellationHours?: number | null;
}) {
  const { t } = useTranslation('bookingPage');
  return (
    <aside className="hidden flex-col gap-3.5 lg:sticky lg:top-6 lg:flex lg:self-start" data-testid="summary-rail">
      <div className="flex flex-col gap-[18px] rounded-[18px] border border-line bg-surface p-6 shadow-card">
        <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-soft">{t('steps.summary.title')}</span>
        <dl className="flex flex-col gap-3">
          {rows.map((row) => (
            <div key={row.label} className="flex justify-between gap-4 text-[14px]">
              <dt className="text-soft">{row.label}</dt>
              <dd className={row.value ? 'text-right font-medium text-ink' : 'text-soft'}>{row.value || '—'}</dd>
            </div>
          ))}
        </dl>
        {total && (
          <>
            <div className="h-px bg-line" />
            <div className="flex items-baseline justify-between">
              <span className="text-[15px] font-semibold text-ink">{t('steps.summary.total')}</span>
              <span className="font-display text-[24px] font-semibold tabular-nums text-ink" data-testid="summary-total">{total}</span>
            </div>
          </>
        )}
        {cta}
        <ReassuranceRow className="flex-wrap gap-3.5" />
      </div>
      {cancellationHours != null && (
        <p className="text-center text-[13px] leading-normal text-soft">{t('steps.freeCancellation', { count: cancellationHours })}</p>
      )}
    </aside>
  );
}
